import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Client, CreateInitiativeNoteCommand, OnePInitiativeDTO } from '../../../api/api-clientInformation';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface CommentDialogData {
  title: string;
  currentComment: string;
  initiativePk: number;
  field: 'comment' | 'afeComment' | 'note';
}

@Component({
  selector: 'app-comment-dialog',
  standalone: false,
  templateUrl: './comment-dialog.component.html',
  styleUrl: './comment-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentDialogComponent implements OnInit {
  apiClient = inject(Client);
  toastrService = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  commentFormControl= new FormControl('', [Validators.maxLength(5000)]);
  initiativeData?: OnePInitiativeDTO;
  isLoading = true;

  constructor(
    private dialogRef: MatDialogRef<CommentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CommentDialogData
  ) {
    this.commentFormControl.setValue(data.currentComment || '');
  }

  ngOnInit(): void {
    if (this.data.field === 'note') {
      this.commentFormControl.setValue('');
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.apiClient.onePGeneralInitiativeGET(this.data.initiativePk).subscribe({
      next: (initiative) => {
        this.initiativeData = initiative;
        const latestComment = this.data.field === 'comment'
          ? (initiative as any).comment
          : (initiative as any).afeComment;
        this.commentFormControl.setValue(latestComment || '');
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FAILED_LOAD_INITIATIVE'));
        this.isLoading = false;
        this.dialogRef.close({ success: false });
        this.cdr.markForCheck();
      }
    });
  }

  onConfirm(): void {
    if (this.commentFormControl.invalid) {
      this.toastrService.error(this.translate.instant('MESSAGES.ERROR.COMMENT_MAX_LENGTH'));
      return;
    }

    const commentValue = this.commentFormControl.value || '';

    if (this.data.field === 'note') {
      const cmd = new CreateInitiativeNoteCommand({
        initiaitveFk: this.data.initiativePk,
        comment: commentValue,
      });
      this.apiClient.createInitiativeNote(cmd).subscribe({
        next: () => {
          this.dialogRef.close({ success: true, comment: commentValue });
        },
        error: () => {
          this.toastrService.error(this.translate.instant('NOTES_ATTACHMENTS.ERROR_CREATING'));
          this.dialogRef.close({ success: false });
        }
      });
      return;
    }

    if (!this.initiativeData) {
      this.toastrService.error(this.translate.instant('MESSAGES.ERROR.INITIATIVE_NOT_LOADED'));
      return;
    }

    const initiativeDTO = {
      ...this.initiativeData,
      [this.data.field]: commentValue
    };

    const command = {
      onePInitiativeDTO: initiativeDTO
    };
    //TODO :NEED A WAY TO UPDATE ONLY COMMENT AND AFE COMMENT
    this.apiClient.updateInitiative(command as any).subscribe({
      next: () => {
        this.dialogRef.close({ success: true, comment: commentValue });
      },
      error: (error) => {
        this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FAILED_UPDATE_COMMENT'));
        this.dialogRef.close({ success: false });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
